import RefundsServices from '../services/refunds.service';

/**
 * Colores de los estados de reembolso, tomados de la BD
 * (`refunds-requests-changelog-status.highlightColor`).
 *
 * Antes cada vista tenía su propio mapa hardcodeado y se habían desincronizado
 * entre sí y con la BD. Este mixin es la única fuente en el front: los
 * componentes llaman a `loadRefundStatusColors()` en `created()` y luego a
 * `refundStatusColor(...)` para pintar.
 *
 * La respuesta se cachea a nivel de módulo, así que varios componentes en la
 * misma sesión comparten una sola llamada.
 */

/** Color de respaldo si la BD no trae uno o la carga falla. */
const FALLBACK_COLOR = '#9E9E9E';

let cachedStatuses = null;
let inFlightRequest = null;

/** Normaliza el nombre para que "Por corregir" y "Corregir" no fallen por tilde/caja. */
function normaliseName(name) {
  return String(name || '')
    .normalize('NFD')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

async function fetchStatuses() {
  if (cachedStatuses) return cachedStatuses;
  if (!inFlightRequest) {
    inFlightRequest = RefundsServices.getChangelogStatuses()
      .then(res => {
        // Solo se cachea el éxito: si falla, el próximo componente reintenta
        // en vez de quedar con la lista vacía toda la sesión.
        if (res.status === 'ok' && Array.isArray(res.data)) {
          cachedStatuses = res.data;
          return cachedStatuses;
        }
        return [];
      })
      .finally(() => {
        inFlightRequest = null;
      });
  }
  return inFlightRequest;
}

export const refundStatusColorsMixin = {
  data() {
    return {
      refundStatuses: cachedStatuses || []
    };
  },
  methods: {
    /** Carga los estados desde la BD. Idempotente: la segunda llamada usa la caché. */
    async loadRefundStatusColors() {
      this.refundStatuses = (await fetchStatuses()) || [];
      return this.refundStatuses;
    },

    /**
     * Color de un estado. Acepta el `sequenceOrder` (número) o el nombre
     * (string), porque las vistas identifican el estado de las dos formas.
     */
    refundStatusColor(sequenceOrderOrName) {
      if (sequenceOrderOrName === null || sequenceOrderOrName === undefined) {
        return FALLBACK_COLOR;
      }

      const match =
        typeof sequenceOrderOrName === 'number'
          ? this.refundStatuses.find(s => s.sequenceOrder === sequenceOrderOrName)
          : this.refundStatuses.find(
              s => normaliseName(s.name) === normaliseName(sequenceOrderOrName)
            );

      return match?.highlightColor || FALLBACK_COLOR;
    }
  }
};
